/**
 * =============================================================================
 * useWlTenantCourtesy
 *
 * Hooks de leitura e mutação para o status de "Cortesia" de um tenant no
 * painel White Label. Cortesia é um rótulo cirúrgico aplicado sobre o tenant
 * (colunas `is_courtesy` / `courtesy_reason` / `courtesy_granted_at` /
 * `courtesy_granted_by` na tabela `tenants`) que não altera plano/assinatura
 * mas exclui o tenant dos KPIs financeiros WL (MRR, ARR, add-ons, séries).
 *
 * RPCs consumidas:
 *   - wl_get_tenant_courtesy(p_tenant_id) — retorna estado atual.
 *   - wl_set_tenant_courtesy(p_tenant_id, p_is_courtesy, p_reason) — alterna.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Tipagens
// ---------------------------------------------------------------------------
export interface WlTenantCourtesy {
  tenant_id: string;
  is_courtesy: boolean;
  courtesy_reason: string | null;
  courtesy_granted_at: string | null;
  granted_by_user_id: string | null;
  granted_by_name: string | null;
}

// ---------------------------------------------------------------------------
// Query: estado atual de cortesia
// ---------------------------------------------------------------------------
export function useWlTenantCourtesy(tenantId: string | null) {
  return useQuery({
    queryKey: ["wl_tenant_courtesy", tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<WlTenantCourtesy | null> => {
      // Usa casting: RPC recém-criada; tipos regenerados após aprovação da migration.
      const { data, error } = await supabase.rpc(
        "wl_get_tenant_courtesy" as never,
        { p_tenant_id: tenantId } as never,
      );
      if (error) throw error;
      const row = ((data ?? []) as WlTenantCourtesy[])[0];
      return row ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation: marcar / desmarcar cortesia
// ---------------------------------------------------------------------------
export function useWlSetTenantCourtesy(tenantId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { isCourtesy: boolean; reason?: string | null }) => {
      if (!tenantId) throw new Error("Tenant inválido");
      const { data, error } = await supabase.rpc(
        "wl_set_tenant_courtesy" as never,
        {
          p_tenant_id: tenantId,
          p_is_courtesy: input.isCourtesy,
          p_reason: input.reason ?? null,
        } as never,
      );
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalida caches locais + listagens/KPIs financeiros do WL.
      qc.invalidateQueries({ queryKey: ["wl_tenant_courtesy", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl_tenant_overview", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl-tenants-list"] });
      qc.invalidateQueries({ queryKey: ["wl-financial-kpis"] });
      qc.invalidateQueries({ queryKey: ["wl-financial-detail"] });
    },
  });
}
